import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { RefreshCw, AlertTriangle, Tag } from "lucide-react";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function ItemMapping() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("unmapped");
  const [busy, setBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [blends, setBlends] = useState([]);
  const [pkgItems, setPkgItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const load = async () => {
    try {
      const { data } = await api.get(`/sales-items?item_status=${view}&only_coffee=false`);
      setItems(data);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); setPage(1); /* eslint-disable-next-line */ }, [view]);
  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => {
    api.get("/blends").then((r) => setBlends(r.data)).catch(() => setBlends([]));
    api.get("/packaging-items").then((r) => setPkgItems(r.data)).catch(() => setPkgItems([]));
  }, []);

  const sync = async () => {
    setBusy(true); setSyncResult(null);
    try {
      const { data } = await api.post("/sales-items/sync");
      setSyncResult(data);
      await load();
    } finally { setBusy(false); }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/sales-items/${id}`, patch);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = search.trim()
    ? items.filter((it) => (it.name || "").toLowerCase().includes(search.toLowerCase())
                         || (it.sku || "").toLowerCase().includes(search.toLowerCase()))
    : items;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <div data-testid="item-mapping-page">
      <PageHeader
        eyebrow="Master data"
        title="Item Mapping"
        description="Map every Zoho coffee item to its blend, packaging components and kg-per-unit. This drives both kg-sold reporting and per-item Manufacturing Cost / margin."
        actions={
          <button onClick={sync} disabled={busy} data-testid="sync-items"
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Sync items from Zoho
          </button>
        }
      />

      {syncResult && (
        <div className="bg-secondary/40 border border-border rounded p-3 text-xs mb-4 font-mono">
          Synced {syncResult.fetched} items · {syncResult.new} new · {syncResult.needs_mapping} need mapping
        </div>
      )}

      <div className="bg-card border border-border rounded p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {[
            ["unmapped", "Needs mapping"],
            ["mapped", "Mapped"],
            ["ignored", "Ignored"],
            ["all", "All"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              data-testid={`item-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                view === k ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card text-muted-foreground border-border hover:bg-secondary"
              }`}>
              {label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <input
              type="search"
              placeholder="Search by name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="item-search"
              className="px-3 py-1.5 border border-border rounded text-xs w-56"
            />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center flex flex-col items-center gap-2">
            <Tag className="w-6 h-6 text-muted-foreground/50" />
            {search ? "No items match your search." : "No items in this filter."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left p-3 label-tiny">Item name</th>
                  <th className="text-left p-3 label-tiny">SKU</th>
                  <th className="text-right p-3 label-tiny">Kg / unit</th>
                  <th className="text-left p-3 label-tiny w-44">Blend / Recipe</th>
                  <th className="text-left p-3 label-tiny">Packaging</th>
                  <th className="text-center p-3 label-tiny w-28" title="Tick to include this item as a row on the Manufacturing Cost page">Mfg Cost</th>
                  <th className="text-right p-3 label-tiny">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((it) => (
                  <ItemRow key={it.item_id} item={it} onUpdate={update} blends={blends} pkgItems={pkgItems} />
                ))}
              </tbody>
            </table>
            {filtered.length > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-3 p-3 border-t border-border bg-secondary/30 text-xs">
                <span className="text-muted-foreground font-mono" data-testid="item-pagination-info">
                  Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={currentPage === 1}
                    data-testid="item-page-first"
                    className="px-2 py-1 border border-border rounded hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    « First
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="item-page-prev"
                    className="px-2 py-1 border border-border rounded hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ‹ Prev
                  </button>
                  <span className="px-2 font-mono" data-testid="item-page-current">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="item-page-next"
                    className="px-2 py-1 border border-border rounded hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={currentPage === totalPages}
                    data-testid="item-page-last"
                    className="px-2 py-1 border border-border rounded hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Last »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-muted-foreground bg-[hsl(31,65%,53%)]/10 border border-[hsl(31,65%,53%)]/30 rounded p-3 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-[hsl(31,65%,53%)] mt-0.5 flex-shrink-0" />
        <span>
          Items without a blend or packaging assigned won't roll up correctly into Manufacturing Cost. The retail/wholesale price for each item lives on the Zoho item itself — those prices are pulled automatically and used for margin calculations.
        </span>
      </div>
    </div>
  );
}

function ItemRow({ item, onUpdate, blends, pkgItems }) {
  const [kg, setKg] = useState(item.kg_per_unit ?? "");
  const [blendId, setBlendId] = useState(item.blend_id || "");
  const [pkgIds, setPkgIds] = useState(item.packaging_item_ids || []);
  const [pkgOpen, setPkgOpen] = useState(false);

  const dirty =
    String(kg) !== String(item.kg_per_unit ?? "") ||
    blendId !== (item.blend_id || "") ||
    JSON.stringify([...pkgIds].sort()) !== JSON.stringify([...(item.packaging_item_ids || [])].sort());

  const togglePkg = (id) => {
    setPkgIds((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  const pkgLabels = pkgIds
    .map((id) => pkgItems.find((p) => p.id === id)?.name)
    .filter(Boolean);

  const save = () => onUpdate(item.item_id, {
    kg_per_unit: kg === "" ? null : parseFloat(kg),
    blend_id: blendId || null,
    packaging_item_ids: pkgIds,
  });

  return (
    <tr className="border-t border-border align-top" data-testid={`item-row-${item.item_id}`}>
      <td className="p-3">
        <p className="font-medium">{item.name}</p>
        {item.unit && <p className="text-[10px] text-muted-foreground font-mono">unit: {item.unit}{item.auto_parsed_kg != null ? ` · auto: ${fmt(item.auto_parsed_kg, 3)} kg` : ""}{item.rate ? ` · rate R${fmt(item.rate)}` : ""}</p>}
      </td>
      <td className="p-3 text-xs text-muted-foreground font-mono">{item.sku || "—"}</td>
      <td className="p-3 text-right">
        <input type="number" step="0.001" min="0"
          value={kg} onChange={(e) => setKg(e.target.value)}
          data-testid={`kg-input-${item.item_id}`}
          className="w-24 px-2 py-1 border border-border rounded font-mono text-right" />
      </td>
      <td className="p-3">
        <select value={blendId} onChange={(e) => setBlendId(e.target.value)}
          data-testid={`blend-select-${item.item_id}`}
          className="w-full px-2 py-1 border border-border rounded text-xs">
          <option value="">— none —</option>
          {blends.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </td>
      <td className="p-3 relative">
        <button type="button" onClick={() => setPkgOpen((o) => !o)}
          data-testid={`pkg-multiselect-${item.item_id}`}
          className="w-full text-left px-2 py-1 border border-border rounded text-xs hover:bg-secondary truncate">
          {pkgLabels.length > 0 ? pkgLabels.join(", ") : <span className="text-muted-foreground">— select —</span>}
        </button>
        {pkgOpen && (
          <div className="absolute z-30 mt-1 left-2 right-2 bg-card border border-border rounded shadow-lg max-h-60 overflow-y-auto">
            {pkgItems.filter((p) => p.is_active !== false).map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary cursor-pointer text-xs"
                data-testid={`pkg-opt-${item.item_id}-${p.id}`}>
                <input type="checkbox" checked={pkgIds.includes(p.id)}
                  onChange={() => togglePkg(p.id)}
                  className="accent-[hsl(20,39%,39%)]" />
                <span>{p.name}</span>
                <span className="ml-auto text-muted-foreground font-mono">R {(p.unit_cost || 0).toFixed(2)}</span>
              </label>
            ))}
            <div className="sticky bottom-0 bg-secondary/80 px-3 py-1 text-right">
              <button onClick={() => setPkgOpen(false)} className="text-xs underline">Close</button>
            </div>
          </div>
        )}
      </td>
      <td className="p-3 text-center">
        <input
          type="checkbox"
          checked={!!item.include_in_mfg_cost}
          onChange={(e) => onUpdate(item.item_id, { include_in_mfg_cost: e.target.checked })}
          data-testid={`include-mfg-${item.item_id}`}
          title="Include this item as a row on the Manufacturing Cost page"
          className="accent-[hsl(20,39%,39%)] w-4 h-4 cursor-pointer"
        />
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        {dirty && (
          <button onClick={save}
            data-testid={`save-${item.item_id}`}
            className="px-2 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded mr-1">
            Save
          </button>
        )}
        {!item.ignored ? (
          <button onClick={() => onUpdate(item.item_id, { ignored: true })}
            data-testid={`ignore-${item.item_id}`}
            className="px-2 py-1 text-xs text-muted-foreground border border-border rounded hover:bg-secondary">
            Ignore
          </button>
        ) : (
          <button onClick={() => onUpdate(item.item_id, { ignored: false })}
            data-testid={`unignore-${item.item_id}`}
            className="px-2 py-1 text-xs text-muted-foreground border border-border rounded hover:bg-secondary">
            Restore
          </button>
        )}
      </td>
    </tr>
  );
}
